import React from 'react'
import { useSpringCurveData } from '../../hooks/useSpringCurveData'
import type { SpringConfig } from '../../types'

interface SpringCurveVisualizerProps {
  config: SpringConfig
  progress: number
}

const PAD = { top: 12, right: 12, bottom: 20, left: 30 }
const W = 288
const H = 150
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom

export function SpringCurveVisualizer({ config, progress }: SpringCurveVisualizerProps) {
  const { points, durationMs, overshootPercent, maxValue, minValue } = useSpringCurveData(config)

  const yMin = Math.min(0, minValue - 0.05)
  const yMax = Math.max(1, maxValue + 0.05)
  const yRange = yMax - yMin

  const toX = (t: number) => PAD.left + (t / durationMs) * PLOT_W
  const toY = (v: number) => PAD.top + (1 - (v - yMin) / yRange) * PLOT_H

  // Build SVG path
  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(p.t).toFixed(1)},${toY(p.value).toFixed(1)}`)
    .join(' ')

  // Playback dot position
  const dotIdx = Math.min(Math.floor(progress * (points.length - 1)), points.length - 1)
  const dotPoint = points[dotIdx]

  // Settling time label
  const settlingLabel = durationMs >= 1000
    ? `${(durationMs / 1000).toFixed(1)}s`
    : `${Math.round(durationMs)}ms`

  // Overshoot peak position for annotation
  const peakPoint = overshootPercent > 0
    ? points.reduce((max, p) => (p.value > max.value ? p : max), points[0])
    : null

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Spring curve visualization">
      {/* Background */}
      <rect x="0.5" y="0.5" width={W - 1} height={H - 1} rx="6" fill="#f9fafb" stroke="#e5e7eb" />

      {/* Target line at y=1 */}
      <line
        x1={PAD.left} y1={toY(1)}
        x2={W - PAD.right} y2={toY(1)}
        stroke="#a5b4fc" strokeWidth="1" strokeDasharray="4 3"
      />

      {/* Zero line at y=0 */}
      <line
        x1={PAD.left} y1={toY(0)}
        x2={W - PAD.right} y2={toY(0)}
        stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 3"
      />

      {/* Y-axis labels */}
      <text x={PAD.left - 4} y={toY(1) + 1} textAnchor="end" className="text-[9px]" fill="#9ca3af" dominantBaseline="middle">1</text>
      <text x={PAD.left - 4} y={toY(0) + 1} textAnchor="end" className="text-[9px]" fill="#9ca3af" dominantBaseline="middle">0</text>

      {/* Spring curve */}
      <path d={pathD} fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

      {/* Overshoot annotation */}
      {peakPoint && overshootPercent > 0 && (
        <text
          x={toX(peakPoint.t)}
          y={toY(peakPoint.value) - 6}
          textAnchor="middle"
          className="text-[9px] font-medium"
          fill="#6366f1"
        >
          +{overshootPercent}%
        </text>
      )}

      {/* Settling time label */}
      <text
        x={W - PAD.right}
        y={H - 4}
        textAnchor="end"
        className="text-[9px]"
        fill="#9ca3af"
      >
        {settlingLabel}
      </text>

      {/* Playback dot */}
      {progress > 0 && dotPoint && (
        <circle
          cx={toX(dotPoint.t)}
          cy={toY(dotPoint.value)}
          r="4"
          fill="#6366f1"
          stroke="white"
          strokeWidth="1.5"
        />
      )}
    </svg>
  )
}
